// @ts-check
/* eslint-disable no-unused-vars */
import { parseQueryString } from '@flowio/web-sdk';
import * as blazeConsumer from '@flowio/blaze-consumer';
import { getSessionId, getOrganization } from '@flowio/session';
import { applePay } from '@flowio/payment';
import { isFeatureEnabled } from './features';
import events, { blazeCheckoutEvents } from './events';
import globalFeatures from './constants/features';
import isUrl from './utilities/isUrl';

export const BLAZE_SETTINGS_KEY = 'flow_blaze';
export const BLAZE_DISABLED_VALUE = 'disabled';
export const BLAZE_INTEGRATION_VALUE = 'integration';
export const BLAZE_INTEGRATION_URL = 'https://integration-pay.flow.io';

/**
 * @typedef FlowBlazeSettings
 * @property {boolean} enabled
 * @property {string} [url]
 */

function getSettings() {
  /** @type {FlowBlazeSettings} */
  let blazeSettings = {
    enabled: false,
  };

  try {
    const savedBlazeSettings = window.sessionStorage.getItem(BLAZE_SETTINGS_KEY);

    if (savedBlazeSettings != null) {
      blazeSettings = JSON.parse(savedBlazeSettings);
    }
  } catch (e) { /* do nothing */ }

  return blazeSettings;
}

function saveSettings(settings = {}) {
  const blazeSettings = getSettings();

  try {
    window.sessionStorage.setItem(BLAZE_SETTINGS_KEY, JSON.stringify({
      ...blazeSettings,
      ...settings,
    }));
  } catch (e) { /* do nothing */ }
}

function getCustomerToken() {
  const customerTokenElement = window.document.querySelector('[flow-customer-token]');
  if (customerTokenElement !== null) {
    return customerTokenElement.getAttribute('value');
  }
  return undefined;
}

/**
 * @param {Record<string, unknown> | null | undefined} attributes
 * @returns {Record<string, string> | undefined}
 */
function serializeAttributes(attributes) {
  if (attributes == null) return undefined;
  return Object.keys(attributes).reduce((memo, key) => {
    const value = attributes[key];
    return {
      ...memo,
      [key]: typeof value === 'string' ? value : JSON.stringify(value),
    };
  }, {});
}

export function configureBlaze(/** @type {FlowBlazeSettings} */{ url, ...otherSettings }) {
  saveSettings({
    ...otherSettings,
    url,
  });

  if (url) {
    blazeConsumer.config({
      blazeUrl: url,
    });
  }
}

/**
 * @description Gets Blaze configuration from session storage
 * and query param for test/dev environments.
 *
 * @usage use the following query string parameter combinations
 * to set blaze configuration overrides
 *
 *  ?blaze
 *    - Force Enable Blaze
 *
 *  ?blaze=disabled
 *    - Force Disable Blaze
 *
 *  ?blaze=integration
 *    - Use Blaze's integration environment
 *
 *  ?blaze=http://localhost:0000/
 *    - For local development purposes (only works in localhost)
 */
export function getBlazeConfiguration() {
  return isFeatureEnabled(
    globalFeatures.GLOBAL_CHECKOUT_BLAZE,
  ).then((isBlazeFeatureEnabled) => {
    const savedSettings = getSettings();
    /** @type {FlowBlazeSettings} */
    const blazeSettings = {
      ...savedSettings,
      enabled: savedSettings.enabled || isBlazeFeatureEnabled,
    };

    const {
      blaze,
    } = parseQueryString(window.location.search);

    if (blaze) {
      blazeSettings.enabled = (blaze !== BLAZE_DISABLED_VALUE);

      if (blaze === BLAZE_DISABLED_VALUE) {
        blazeSettings.url = undefined;
      } else if (blaze === BLAZE_INTEGRATION_VALUE) {
        blazeSettings.url = BLAZE_INTEGRATION_URL;
      } else if (typeof blaze === 'string' && isUrl(blaze)) {
        blazeSettings.url = blaze;
      }
      configureBlaze(blazeSettings);
    }

    return blazeSettings;
  });
}

export function isEnabled() {
  return getBlazeConfiguration()
    .then(({ enabled }) => enabled);
}

export function initialize() {
  return isEnabled().then((enabled) => {
    if (enabled) {
      const blazeInitParams = {
        sessionId: getSessionId(),
      };
      const customerToken = getCustomerToken();
      if (customerToken) blazeInitParams.customerToken = customerToken;
      blazeConsumer.initialize(blazeInitParams);
      const {
        BLAZE_CHECKOUT_PROGRESS,
        BLAZE_CHECKOUT_COMPLETE,
        BLAZE_ENTER_CHECKOUT,
        BLAZE_EXIT_CHECKOUT,
        BLAZE_BEGIN_CHECKOUT,
      } = events.constants;

      blazeConsumer.subscribe((message) => {
        switch (message.discriminator) {
        case 'checkout_event': blazeCheckoutEvents(message);
          break;
        case 'checkout_progress':
          events.trigger(BLAZE_CHECKOUT_PROGRESS, message);
          break;
        case 'checkout_complete':
          events.trigger(BLAZE_CHECKOUT_COMPLETE, message);
          break;
        case 'start_checkout':
          events.trigger(BLAZE_ENTER_CHECKOUT, message);
          break;
        case 'exit_checkout':
          events.trigger(BLAZE_EXIT_CHECKOUT, message);
          break;
        case 'redirect_confirmation':
          window.location.assign(message.url);
          break;
        case 'applepay_availability_request':
          applePay.getAvailability()
            .then((response) => {
              blazeConsumer.setApplePayAvailability(response);
            });
          break;
        default:
          break;
        }
      });
    }
  });
}

/**
 * @typedef CartItem
 * @property {string} number
 * @property {number} [quantity]
 * @property {Record<string, string>} [attributes]
 */

/** @param {CartItem[]} cartItems */
export function setCheckoutItems(
  cartItems,
) {
  blazeConsumer.setCartItems(cartItems.map(_ => ({
    attributes: serializeAttributes(_.attributes),
    item_number: _.number,
    quantity: _.quantity,
  })));
}

/** @param {CartItem[]} cartItems */
export function addCheckoutItems(
  cartItems,
) {
  cartItems.forEach((_) => {
    blazeConsumer.addItemToCart({
      attributes: serializeAttributes(_.attributes),
      item_number: _.number,
      quantity: _.quantity,
    });
  });
}

// Used to update quantity or remove items
/** @param {CartItem[]} cartItems */
export function updateCheckoutItems(
  cartItems,
) {
  blazeConsumer.updateCartItems(cartItems.map(_ => ({
    attributes: serializeAttributes(_.attributes),
    item_number: _.number,
    quantity: _.quantity,
  })));
}

export function clearCheckoutItems() {
  blazeConsumer.setCartItems([]);
}

/** @param {CartItem[]} [cartItems] */
export function openCheckout(
  cartItems,
) {
  if (Array.isArray(cartItems) && cartItems.length) {
    setCheckoutItems(cartItems);
  }

  blazeConsumer.startCheckout();
}

export function setPromotionCode(promotionCode) {
  blazeConsumer.setPromotionCode(promotionCode);
}



// WEBPACK FOOTER //
// ./static/js/v0/blaze.js